import { Button, SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageErrorState } from '../components/shared/PageErrorState';
import { VendorCard, VendorCardSkeleton, VendorLogo } from '../components/shared/VendorCard';
import {
  ContextDrawer,
  MainContentHeaderActionsContext,
  MainContentSidePanelContext,
} from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { apiFetch } from '../lib/apiClient';
import './workspace-products-page.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WorkspaceProduct {
  id: number;
  uuid: string;
  vendorId: number;
  vendorKey: string;
  vendorName: string;
  productKey: string;
  productName: string;
  description: string | null;
  isPrimary: boolean;
  displayOrder: number;
}

// ─── Add product drawer panel ─────────────────────────────────────────────────

interface AddProductPanelProps {
  catalog: CatalogItem[];
  onAdded: () => void;
  onCancel: () => void;
}

interface CatalogItem {
  vendorKey: string;
  vendorName: string;
  productKey: string;
  productName: string;
  description: string | null;
}

function AddProductPanel({ catalog, onAdded, onCancel }: AddProductPanelProps) {
  const [isAdding, setIsAdding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedItem, setSelectedItem] = React.useState<CatalogItem | null>(null);

  const handleConnect = async (item: CatalogItem) => {
    setIsAdding(true);
    setError(null);
    try {
      await apiFetch('/api/workspace/admin/products', {
        method: 'POST',
        body: JSON.stringify({
          vendorKey: item.vendorKey,
          vendorName: item.vendorName,
          productKey: item.productKey,
          productName: item.productName,
          description: item.description ?? undefined,
        }),
      });
      onAdded();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add product';
      setError(msg === 'PRODUCT_ALREADY_ADDED' ? 'This product has already been added.' : msg);
      setIsAdding(false);
    }
  };

  if (!error && catalog.length === 0) {
    return (
      <div className="wp-drawer-empty">
        <p className="wp-drawer-empty-text">No products available to add.</p>
        <button type="button" className="wp-drawer-btn wp-drawer-btn--secondary" onClick={onCancel}>
          Close
        </button>
      </div>
    );
  }

  // ── Step 2: Confirmation / connect pane ──────────────────────────────────
  if (selectedItem) {
    return (
      <div className="wp-drawer-form">
        <div className="wp-catalog-connect-header">
          <div className="wp-catalog-item-logo">
            <VendorLogo
              vendorKey={selectedItem.vendorKey}
              productKey={selectedItem.productKey}
              productName={selectedItem.productName}
              size={24}
            />
          </div>
          <div className="wp-catalog-item-info">
            <span className="wp-catalog-item-name">{selectedItem.productName}</span>
            <span className="wp-catalog-item-vendor">{selectedItem.vendorName}</span>
          </div>
        </div>
        {selectedItem.description && <p className="wp-drawer-hint">{selectedItem.description}</p>}
        {error && <span className="wp-drawer-error">{error}</span>}
        <div className="wp-drawer-actions">
          <button
            type="button"
            className="wp-drawer-btn wp-drawer-btn--primary"
            onClick={() => void handleConnect(selectedItem)}
            disabled={isAdding}
          >
            {isAdding ? 'Adding…' : 'Add'}
          </button>
          <button
            type="button"
            className="wp-drawer-btn wp-drawer-btn--secondary"
            onClick={() => setSelectedItem(null)}
            disabled={isAdding}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  // ── Step 1: Catalog list ─────────────────────────────────────────────────
  return (
    <div className="wp-drawer-form">
      {error && <span className="wp-drawer-error">{error}</span>}
      <div className="wp-catalog-panel">
        {catalog.map((item) => (
          <button
            key={`${item.productKey}`}
            type="button"
            className="wp-catalog-item"
            onClick={() => setSelectedItem(item)}
          >
            <div className="wp-catalog-item-logo">
              <VendorLogo
                vendorKey={item.vendorKey}
                productKey={item.productKey}
                productName={item.productName}
                size={24}
              />
            </div>
            <div className="wp-catalog-item-info">
              <span className="wp-catalog-item-name">{item.productName}</span>
              <span className="wp-catalog-item-vendor">{item.vendorName}</span>
            </div>
          </button>
        ))}
      </div>
      <button type="button" className="wp-drawer-btn wp-drawer-btn--secondary" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function WorkspaceProductsPage() {
  useWorkspace();
  const navigate = useNavigate();
  const [products, setProducts] = React.useState<WorkspaceProduct[]>([]);
  const [catalog, setCatalog] = React.useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [addDrawerOpen, setAddDrawerOpen] = React.useState(false);

  const headerActionsCtx = React.useContext(MainContentHeaderActionsContext);
  const sidePanelCtx = React.useContext(MainContentSidePanelContext);

  const loadData = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [productsRes, catalogRes] = await Promise.all([
        apiFetch<{ products: WorkspaceProduct[] }>('/api/workspace/admin/products'),
        apiFetch<{ catalog: CatalogItem[] }>('/api/workspace/admin/products/catalog'),
      ]);
      setProducts(productsRes.products);
      setCatalog(catalogRes.catalog);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  // Project "Add product" button into the MainContentShell header
  React.useEffect(() => {
    if (!headerActionsCtx) return;
    if (isLoading) {
      headerActionsCtx.setHeaderActions(
        <div className="wp-skeleton-header-btn">
          <SkeletonBlock />
        </div>
      );
    } else {
      headerActionsCtx.setHeaderActions(
        <Button size="sm" variant="primary" onClick={() => setAddDrawerOpen(true)}>
          Add product
        </Button>
      );
    }
    return () => {
      headerActionsCtx.setHeaderActions(null);
    };
  }, [headerActionsCtx, isLoading]);

  function handleCardClick(uuid: string, p: WorkspaceProduct) {
    navigate(`/administration/products/${uuid}/global-settings`, {
      state: { productName: p.productName },
    });
  }

  // Project the inline ContextDrawer as a flex sibling of the content column
  // so it pushes content left (same behaviour as RightUtilityPane).
  React.useEffect(() => {
    if (!sidePanelCtx) return;
    sidePanelCtx.setPanel(
      <ContextDrawer
        inline
        open={!isLoading && addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        title="Add product"
      >
        {!isLoading && addDrawerOpen && (
          <AddProductPanel
            catalog={catalog}
            onAdded={() => {
              setAddDrawerOpen(false);
              void loadData();
            }}
            onCancel={() => setAddDrawerOpen(false)}
          />
        )}
      </ContextDrawer>
    );
    return () => sidePanelCtx.setPanel(null);
  }, [sidePanelCtx, isLoading, addDrawerOpen, catalog, loadData]);

  if (loadError) {
    return <PageErrorState message={loadError} onRetry={loadData} />;
  }

  if (isLoading) {
    return (
      <div className="wp-page" aria-busy="true" aria-label="Loading products">
        <div className="wp-section">
          <div className="wp-card-grid">
            <VendorCardSkeleton />
            <VendorCardSkeleton />
            <VendorCardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wp-page">
      {/* ── Empty state ──────────────────────────────────────────── */}
      {products.length === 0 && (
        <div className="wp-empty">
          <p className="wp-empty-title">No products added</p>
          <p className="wp-empty-text">Click Add product to get started.</p>
        </div>
      )}

      {/* ── Products ────────────────────────────────────────────── */}
      {products.length > 0 && (
        <div className="wp-section">
          <div className="wp-card-grid">
            {products.map((p) => (
              <VendorCard
                key={p.uuid}
                logo={
                  <VendorLogo
                    vendorKey={p.vendorKey}
                    productKey={p.productKey}
                    productName={p.productName}
                  />
                }
                name={p.productName}
                subtitle={p.vendorName}
                onClick={() => handleCardClick(p.uuid, p)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Context drawer - projected inline via MainContentSidePanelContext ── */}
    </div>
  );
}
